import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from 'react-query'

import { dateFnsOps, filterTruthyMapping, isoDateUTC } from '@percept/utils'

import { makeAuthorizedApiClient, paginationAdapter, useNotifications } from '@percept/hooks'

import { zonedTimeToUtc } from 'date-fns-tz'

import { capitalize } from 'lodash-es'

import { isS3ImageUrl } from './lib'

import {
  MediaAcademyResource,
  MediaAcademyResourceCategory,
  MediaAcademyWebinar,
  MediaAcademyWebinarCategory,
} from './typings'

import { InfinitePaginationWrapper, Notification, PickTypedKeys, ServerPaginatedResponse, TypedKeys } from '@percept/types'


const { VODAFONE_CUSTOM_API_ROOT } = process.env


const mediaAcademyClient = makeAuthorizedApiClient({
  baseURL: `${ VODAFONE_CUSTOM_API_ROOT }/media-academy/v1`,
})

const mediaAcademyAdminClient = makeAuthorizedApiClient({
  baseURL: `${ VODAFONE_CUSTOM_API_ROOT }/media-academy/v1/admin`,
})


export const WEBINAR_QUERY_KEY = 'mediaAcademyWebinars'
export const RESOURCE_QUERY_KEY = 'mediaAcademyResources'


const utcDatetimeAdapter = <T extends PickTypedKeys<T, string>>(
  item: T,
  datetimeKeys: TypedKeys<T, string>[],
): T => {
  return {
    ...item,
    ...(
      datetimeKeys.reduce( (acc, k) => {
        if( !item[k].endsWith('Z') ){
          acc[k] = item[k] + 'Z'
        }
        return acc
      }, {} as PickTypedKeys<T, string>)
    )
  }
}

const paginatedResponseDatetimeAdapter = <T extends PickTypedKeys<T, string>>(
  response: ServerPaginatedResponse<T>,
  datetimeKeys: TypedKeys<T, string>[],
): ServerPaginatedResponse<T> => {
  return {
    ...response,
    items: response.items.map( item => (
      utcDatetimeAdapter(item, datetimeKeys)
    ))
  }
}

export type DeleteEntityParams = { id: string }

type MediaAcademyWebinarUpdateBase = (
  Omit<MediaAcademyWebinar, 'id' | 'start_datetime'> & {
    start: Date
    image_upload?: File | null
  }
)

export type CreateMediaAcademyWebinarParams = MediaAcademyWebinarUpdateBase


const getWebinarEventFormData = (
  params: CreateMediaAcademyWebinarParams,
  include_image_overwrite_flag = false,
): FormData => {
  const formData = new FormData()
  // Required params
  const start_datetime = zonedTimeToUtc(params.start, params.timezone).toISOString()
  formData.append('start_datetime', start_datetime)
  const requiredStringKeys: TypedKeys<CreateMediaAcademyWebinarParams, string>[] = [
    'title',
    'category',
    'timezone',
  ]
  for( const key of requiredStringKeys ){
    formData.append(key, params[key])
  }
  // Optional params
  const optionalKeys: TypedKeys<CreateMediaAcademyWebinarParams, string | null>[] = [
    'webinar_url',
    'summary',
    'additional_url',
  ]
  for( const key of optionalKeys ){
    if( params[key] ){
      formData.append(key, params[key])
    }
  }
  // Image attributes
  let overwrite_image = false
  if( params.image_url && !isS3ImageUrl(params.image_url) ){
    formData.append('image_url', params.image_url)
    overwrite_image = true
  }
  if( params.image_upload ){
    formData.append('image_upload', params.image_upload)
    overwrite_image = true
  }
  if( include_image_overwrite_flag ){
    formData.append('overwrite_image', String(overwrite_image))
  }
  return formData
}


const getMutationOptions = <T>({
  mutationOptions,
  addNotification,
  nameKey,
  entityType,
  mutationType,
}: {
  mutationOptions: UseMutationOptions<void, unknown, T>,
  addNotification: (notification: Notification) => void,
  nameKey?: keyof T,
  entityType: string,
  mutationType: 'create' | 'edit' | 'delete'
}): UseMutationOptions<void, unknown, T> => {
  const actioning = mutationType === 'edit' ? 'Editing' : capitalize(`${mutationType.slice(0, mutationType.length - 1)}ing`)
  const actioned = mutationType === 'edit' ? `${mutationType}ed` : `${mutationType}d`
  const notificationId = `${entityType}-${mutationType}`
  return {
    onMutate: (params): void => {
      const entityRepr = nameKey ? `${entityType} '${params[nameKey]}'` : entityType
      addNotification({
        id: notificationId,
        type: 'info',
        loading: true,
        ttl: 0,
        message: (
          `${actioning} ${entityRepr}...`
        )
      })
      mutationOptions.onMutate && mutationOptions.onMutate(params)
    },
    onSuccess: (data, params, context): void => {
      const entityRepr = (
        nameKey ?
          `${capitalize(entityType)} '${params[nameKey]}'`
          : capitalize(entityType)
      )
      addNotification({
        id: notificationId,
        type: 'success',
        message: (
          `${entityRepr} was successfully ${actioned}`
        ),
      })
      mutationOptions.onSuccess && mutationOptions.onSuccess(data, params, context)
    },
    onError: (error, params, context): void => {
      const entityRepr = (
        nameKey ?
          `${capitalize(entityType)} '${params[nameKey]}'`
          : capitalize(entityType)
      )
      addNotification({
        id: notificationId,
        type: 'error',
        ttl: 0,
        message: (
          `${entityRepr} could not be ${actioned}`
        ),
      })
      mutationOptions.onError && mutationOptions.onError(error, params, context)
    },
  }
}


export const useAddMediaAcademyWebinar = (
  mutationOptions?: UseMutationOptions<void, unknown, CreateMediaAcademyWebinarParams>
): UseMutationResult<void, unknown, CreateMediaAcademyWebinarParams> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async (params) => {
      return await mediaAcademyAdminClient.post(
        '/webinar-events',
        getWebinarEventFormData(params),
      )
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'create',
      entityType: 'webinar',
      nameKey: 'title',
      addNotification,
    })
  })
}


export type EditMediaAcademyWebinarParams = CreateMediaAcademyWebinarParams & {
  id: string
}

export const useEditMediaAcademyWebinar = (
  mutationOptions?: UseMutationOptions<void, unknown, EditMediaAcademyWebinarParams>
): UseMutationResult<void, unknown, EditMediaAcademyWebinarParams> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async ({ id, ...params }) => {
      return await mediaAcademyAdminClient.put(
        `/webinar-events/${id}`,
        getWebinarEventFormData(params, true),
      )
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'edit',
      entityType: 'webinar',
      nameKey: 'title',
      addNotification,
    })
  })
}


export const useDeleteMediaAcademyWebinar = (
  mutationOptions?: UseMutationOptions<void, unknown, DeleteEntityParams>
): UseMutationResult<void, unknown, DeleteEntityParams> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async ({ id }) => {
      return await mediaAcademyAdminClient.delete(`/webinar-events/${id}`)
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'delete',
      entityType: 'webinar',
      addNotification,
    })
  })
}


type BasePaginationProps = {
  order: 'ASC' | 'DESC'
  limit?: number
}


type WebinarQueryProps = BasePaginationProps & {
  start?: Date | null
  end?: Date | null
  category?: MediaAcademyWebinarCategory | null
}


export type MediaAcademyWebinarsHookProps = (
  WebinarQueryProps 
  & UseInfiniteQueryOptions<InfinitePaginationWrapper<MediaAcademyWebinar>>
)


export async function getPaginatedWebinars({
  start,
  end,
  order,
  limit,
  category,
  page,
}: WebinarQueryProps & { page: number }): Promise<InfinitePaginationWrapper<MediaAcademyWebinar>> {
  const startParam = start ? start.toISOString() : null
  const endParam = end ? end.toISOString() : null
  const response = await mediaAcademyClient.get<ServerPaginatedResponse<MediaAcademyWebinar>>('/webinar-events', {
    params: {
      ...filterTruthyMapping({
        start: startParam,
        end: endParam,
        category,
      }),
      page_number: page,
      limit,
      order_is_asc: order === 'ASC',
      order_by: 'start_datetime',
    }
  })
  const datetimeAdapted = paginatedResponseDatetimeAdapter(response.data, ['start_datetime'])
  return paginationAdapter(datetimeAdapted, page)
}

export const useMediaAcademyWebinars = ({
  start,
  end,
  order,
  category,
  limit = 5,
  ...queryOptions
}: MediaAcademyWebinarsHookProps): UseInfiniteQueryResult<InfinitePaginationWrapper<MediaAcademyWebinar>> => {
  // NOTE - we need to create a more stable query key, otherwise this will constantly refetch as the time
  // changes on each render of any component using the hook. For now we're pinning to the hour. 
  const startKey = start && isoDateUTC(dateFnsOps.startOfHour(start))
  const endKey = end && isoDateUTC(dateFnsOps.startOfHour(end))
  return useInfiniteQuery({
    queryKey: [WEBINAR_QUERY_KEY, category, startKey, endKey, order, limit],
    getNextPageParam: (prevData: InfinitePaginationWrapper<MediaAcademyWebinar>) => prevData.nextPage,
    queryFn: async ({ pageParam = 1 }) => {
      return await getPaginatedWebinars({
        start,
        end,
        category,
        limit,
        order,
        page: pageParam,
      })
    },
    retry: false,
    staleTime: 1000 * 60 * 2,
    ...queryOptions,
  })
}


export type CreateMediaAcademyResouceParams = Omit<MediaAcademyResource, 'id'>

export const useCreateMediaAcademyResource = (
  mutationOptions?: UseMutationOptions<void, unknown, CreateMediaAcademyResouceParams>
): UseMutationResult<void, unknown, CreateMediaAcademyResouceParams> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async (params) => {
      return await mediaAcademyAdminClient.post(
        '/resources',
        {
          ...params,
          published_date: isoDateUTC(params.published_date),
        },
      )
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'create',
      entityType: 'resource',
      nameKey: 'name',
      addNotification,
    })
  })
}

export const useEditMediaAcademyResource = (
  mutationOptions?: UseMutationOptions<void, unknown, MediaAcademyResource>
): UseMutationResult<void, unknown, MediaAcademyResource> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async (resource) => {
      return await mediaAcademyAdminClient.put(
        `/resources/${resource.id}`,
        {
          ...resource,
          published_date: isoDateUTC(resource.published_date),
        },
      )
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'edit',
      entityType: 'resource',
      nameKey: 'name',
      addNotification,
    })
  })
}

export const useDeleteMediaAcademyResource = (
  mutationOptions?: UseMutationOptions<void, unknown, DeleteEntityParams>
): UseMutationResult<void, unknown, DeleteEntityParams> => {
  const [, addNotification] = useNotifications()
  return useMutation({
    mutationFn: async ({ id }) => {
      return await mediaAcademyAdminClient.delete(`/resources/${id}`)
    },
    ...mutationOptions,
    ...mutationOptions && getMutationOptions({
      mutationOptions,
      mutationType: 'delete',
      entityType: 'resource',
      addNotification,
    })
  })
}


type ResourceQueryProps = BasePaginationProps & {
  category?: MediaAcademyResourceCategory | null
}


export type MediaAcademyResourcesHookProps = (
  ResourceQueryProps
  & UseInfiniteQueryOptions<InfinitePaginationWrapper<MediaAcademyResource>>
)


export async function getPaginatedResources({
  order,
  limit,
  category,
  page,
}: ResourceQueryProps & { page: number }): Promise<InfinitePaginationWrapper<MediaAcademyResource>> {
  const response = await mediaAcademyClient.get<ServerPaginatedResponse<MediaAcademyResource>>('/resources', {
    params: {
      ...filterTruthyMapping({
        category,
      }),
      page_number: page,
      limit,
      order_is_asc: order === 'ASC',
      order_by: 'published_date',
    }
  })
  return paginationAdapter(response.data, page)
}


export const useMediaAcademyResources = ({
  order,
  category,
  limit = 20,
  ...queryOptions
}: MediaAcademyResourcesHookProps): UseInfiniteQueryResult<InfinitePaginationWrapper<MediaAcademyResource>> => {
  return useInfiniteQuery({
    queryKey: [RESOURCE_QUERY_KEY, category, order, limit],
    getNextPageParam: (prevData: InfinitePaginationWrapper<MediaAcademyResource>) => prevData.nextPage,
    queryFn: async ({ pageParam = 1 }) => {
      return await getPaginatedResources({
        order,
        limit,
        category,
        page: pageParam,
      })
    },
    retry: false,
    staleTime: 1000 * 60 * 2,
    ...queryOptions,
  })
}
